/**
 * Gerador de impulse responses (reverb) e presets de efeitos.
 * Tudo procedural: não baixamos IRs de terceiros.
 */
import type { AudioContextLike } from './types.js';
import type { FxConfig, FxPresetId, ReverbPresetId } from '../types.js';

export interface ReverbSpec {
  /** Tempo de decaimento (s). */
  decay: number;
  /** Densidade/brilho: 0..1 (maior = mais agudo no tail). */
  brightness: number;
  /** Ganho das reflexões iniciais. */
  earlyGain: number;
  /** Pré-atraso (ms). */
  preDelayMs: number;
  label: string;
}

export const REVERB_SPECS: Record<ReverbPresetId, ReverbSpec> = {
  room: { decay: 0.45, brightness: 0.55, earlyGain: 0.9, preDelayMs: 6, label: 'ROOM' },
  studio: { decay: 0.9, brightness: 0.6, earlyGain: 0.75, preDelayMs: 12, label: 'STUDIO' },
  hall: { decay: 2.2, brightness: 0.42, earlyGain: 0.5, preDelayMs: 26, label: 'HALL' },
  plate: { decay: 1.4, brightness: 0.78, earlyGain: 0.65, preDelayMs: 4, label: 'PLATE' },
};

/** Cria um IR estéreo decorrelacionado com reflexões iniciais discretas. */
export function createImpulseResponse(
  ctx: AudioContextLike,
  spec: ReverbSpec,
  secondsOverride?: number
): AudioBuffer {
  const rate = ctx.sampleRate;
  const seconds = Math.max(0.05, secondsOverride ?? spec.decay);
  const length = Math.max(1, Math.floor(rate * seconds));
  const buffer = ctx.createBuffer(2, length, rate);
  const preDelay = Math.floor((spec.preDelayMs / 1000) * rate);

  for (let ch = 0; ch < 2; ch++) {
    const data = buffer.getChannelData(ch);
    // Reflexões iniciais: pequenos atrasos pseudo-aleatórios determinísticos.
    const taps = 7;
    for (let t = 0; t < taps; t++) {
      const pos =
        preDelay +
        Math.floor(
          ((t + 1) / taps) * Math.min(length - 1, rate * 0.09) +
            ((t * 37 + ch * 13) % 23)
        );
      if (pos >= 0 && pos < length) {
        const amp = spec.earlyGain * Math.pow(1 - (t + 1) / (taps + 2), 1.4) * (ch === 0 ? 1 : 0.92);
        data[pos] += (t % 2 === 0 ? 1 : -1) * amp * 0.5;
      }
    }
    // Cauda: ruído com decaimento exponencial + filtro simples (brilho).
    let lp = 0;
    const bright = spec.brightness;
    for (let i = preDelay; i < length; i++) {
      const t = (i - preDelay) / (length - preDelay || 1);
      const env = Math.pow(1 - t, 2.1) * Math.exp(-3.2 * t);
      const noise = Math.random() * 2 - 1;
      lp = lp + bright * (noise - lp);
      const v = lp * env * 0.55;
      data[i] += ch === 0 ? v : v * (0.94 + 0.06 * Math.sin(i * 0.0011));
    }
    // Fade-out final para evitar clique.
    const fade = Math.min(length, Math.floor(rate * 0.03));
    for (let i = 0; i < fade; i++) {
      const g = i / fade;
      data[length - 1 - i] *= g;
    }
  }
  return buffer;
}

export const defaultFx = (): FxConfig => ({
  preset: 'clean',
  gainDb: 0,
  highPassHz: 90,
  highPassOn: true,
  eqMode: 'simple',
  lowGainDb: 0,
  midGainDb: 0,
  highGainDb: 0,
  eqBands: DEFAULT_EQ_BANDS.map((b) => ({ ...b })),
  presenceDb: 0,
  comp: {
    enabled: false,
    preset: 'medio',
    thresholdDb: -20,
    ratio: 3,
    attackMs: 8,
    releaseMs: 140,
    kneeDb: 8,
    makeupDb: 0,
  },
  reverb: { enabled: false, preset: 'studio', sendDb: -18 },
  delay: { enabled: false, timeMs: 220, feedback: 0.25, sendDb: -22 },
  gate: { enabled: false, thresholdDb: -52, attackMs: 6, releaseMs: 220, holdMs: 90 },
  deEsser: { enabled: false, strength: 'suave', freqHz: 6800 },
  noiseReduction: { enabled: false, amount: 0.5, floorDb: -24 },
  double: { enabled: false, delayMs: 18, pan: 0.35, levelDb: -7, detuneCents: 6 },
});

export const DEFAULT_EQ_BANDS = [
  { freqHz: 80, gainDb: 0, q: 1, type: 'lowshelf' as BiquadFilterType },
  { freqHz: 200, gainDb: 0, q: 1.1, type: 'peaking' as BiquadFilterType },
  { freqHz: 500, gainDb: 0, q: 1.1, type: 'peaking' as BiquadFilterType },
  { freqHz: 1000, gainDb: 0, q: 1.1, type: 'peaking' as BiquadFilterType },
  { freqHz: 3000, gainDb: 0, q: 1.1, type: 'peaking' as BiquadFilterType },
  { freqHz: 8000, gainDb: 0, q: 1.1, type: 'peaking' as BiquadFilterType },
  { freqHz: 12000, gainDb: 0, q: 0.9, type: 'highshelf' as BiquadFilterType },
];

export const FX_PRESETS: Record<FxPresetId, { label: string; desc: string; patch: Partial<FxConfig> }> = {
  dry: {
    label: 'DRY',
    desc: 'Sem efeitos. Só o que o microfone captou.',
    patch: {
      preset: 'dry',
      highPassOn: false,
      presenceDb: 0,
      lowGainDb: 0,
      midGainDb: 0,
      highGainDb: 0,
      comp: { enabled: false, preset: 'medio', thresholdDb: -20, ratio: 3, attackMs: 8, releaseMs: 140, kneeDb: 8, makeupDb: 0 },
      reverb: { enabled: false, preset: 'studio', sendDb: -18 },
      delay: { enabled: false, timeMs: 220, feedback: 0.25, sendDb: -22 },
      gate: { enabled: false, thresholdDb: -52, attackMs: 6, releaseMs: 220, holdMs: 90 },
      deEsser: { enabled: false, strength: 'suave', freqHz: 6800 },
    },
  },
  clean: {
    label: 'CLEAN',
    desc: 'Som natural: high-pass leve e compressão transparente.',
    patch: {
      preset: 'clean',
      highPassOn: true,
      highPassHz: 85,
      presenceDb: 0.5,
      lowGainDb: -0.5,
      midGainDb: 0,
      highGainDb: 0.5,
      comp: { enabled: true, preset: 'suave', thresholdDb: -22, ratio: 2, attackMs: 12, releaseMs: 160, kneeDb: 12, makeupDb: 1 },
      reverb: { enabled: false, preset: 'room', sendDb: -22 },
      delay: { enabled: false, timeMs: 220, feedback: 0.2, sendDb: -24 },
      deEsser: { enabled: false, strength: 'suave', freqHz: 6800 },
      gate: { enabled: false, thresholdDb: -52, attackMs: 6, releaseMs: 220, holdMs: 90 },
    },
  },
  studio: {
    label: 'STUDIO',
    desc: 'Compressão leve + EQ + reverb discreto.',
    patch: {
      preset: 'studio',
      highPassOn: true,
      highPassHz: 95,
      presenceDb: 1.5,
      lowGainDb: -1,
      midGainDb: -0.5,
      highGainDb: 1.5,
      comp: { enabled: true, preset: 'medio', thresholdDb: -20, ratio: 3, attackMs: 8, releaseMs: 140, kneeDb: 8, makeupDb: 2 },
      reverb: { enabled: true, preset: 'studio', sendDb: -17 },
      delay: { enabled: false, timeMs: 240, feedback: 0.22, sendDb: -24 },
      deEsser: { enabled: true, strength: 'suave', freqHz: 6800 },
      gate: { enabled: false, thresholdDb: -52, attackMs: 6, releaseMs: 220, holdMs: 90 },
    },
  },
  pop: {
    label: 'POP',
    desc: 'Compressão + EQ brilhante + reverb + delay leve.',
    patch: {
      preset: 'pop',
      highPassOn: true,
      highPassHz: 100,
      presenceDb: 2.5,
      lowGainDb: -1.5,
      midGainDb: -1,
      highGainDb: 3,
      comp: { enabled: true, preset: 'medio', thresholdDb: -19, ratio: 3.5, attackMs: 6, releaseMs: 120, kneeDb: 6, makeupDb: 3 },
      reverb: { enabled: true, preset: 'plate', sendDb: -14 },
      delay: { enabled: true, timeMs: 260, feedback: 0.28, sendDb: -21 },
      deEsser: { enabled: true, strength: 'medio', freqHz: 7200 },
      gate: { enabled: false, thresholdDb: -52, attackMs: 6, releaseMs: 220, holdMs: 90 },
    },
  },
  rap: {
    label: 'RAP',
    desc: 'Compressão forte + presença + delay curto.',
    patch: {
      preset: 'rap',
      highPassOn: true,
      highPassHz: 110,
      presenceDb: 3,
      lowGainDb: 0.5,
      midGainDb: 1,
      highGainDb: 2.5,
      comp: { enabled: true, preset: 'forte', thresholdDb: -17, ratio: 5, attackMs: 3, releaseMs: 90, kneeDb: 4, makeupDb: 3 },
      reverb: { enabled: true, preset: 'room', sendDb: -20 },
      delay: { enabled: true, timeMs: 150, feedback: 0.18, sendDb: -20 },
      deEsser: { enabled: true, strength: 'medio', freqHz: 7000 },
      gate: { enabled: true, thresholdDb: -48, attackMs: 4, releaseMs: 160, holdMs: 60 },
    },
  },
  ballad: {
    label: 'BALLAD',
    desc: 'Reverb maior + compressão suave.',
    patch: {
      preset: 'ballad',
      highPassOn: true,
      highPassHz: 90,
      presenceDb: 1,
      lowGainDb: -1,
      midGainDb: 0,
      highGainDb: 1.5,
      comp: { enabled: true, preset: 'suave', thresholdDb: -23, ratio: 2.2, attackMs: 15, releaseMs: 200, kneeDb: 14, makeupDb: 2 },
      reverb: { enabled: true, preset: 'hall', sendDb: -12 },
      delay: { enabled: true, timeMs: 320, feedback: 0.3, sendDb: -24 },
      deEsser: { enabled: true, strength: 'suave', freqHz: 6600 },
      gate: { enabled: false, thresholdDb: -52, attackMs: 6, releaseMs: 220, holdMs: 90 },
    },
  },
};

export function applyPreset(base: FxConfig, id: FxPresetId): FxConfig {
  const p = FX_PRESETS[id];
  return {
    ...base,
    ...p.patch,
    comp: { ...base.comp, ...p.patch.comp },
    reverb: { ...base.reverb, ...p.patch.reverb },
    delay: { ...base.delay, ...p.patch.delay },
    gate: { ...base.gate, ...p.patch.gate },
    deEsser: { ...base.deEsser, ...p.patch.deEsser },
  };
}

export const COMP_PRESETS = {
  suave: { thresholdDb: -23, ratio: 2.2, attackMs: 14, releaseMs: 190, kneeDb: 14 },
  medio: { thresholdDb: -20, ratio: 3.2, attackMs: 7, releaseMs: 130, kneeDb: 8 },
  forte: { thresholdDb: -17, ratio: 5.5, attackMs: 3, releaseMs: 85, kneeDb: 3 },
} as const;
